/**
 * Encrypt/decrypt helpers for Space structural metadata (names, descriptions).
 * Uses the cached Argon2 per-Space key with context AAD — the same helper as
 * channel messages.
 */

import {
  DEFAULT_ADMIN_ROLE_NAME,
  DEFAULT_MEMBER_ROLE_NAME,
  DEFAULT_SPACE_CATEGORY_NAME,
  DEFAULT_SPACE_CHANNEL_NAME,
  SPACE_IDENTITY_MEDIA_CONTENT_TYPES,
  SpaceThemeSchema,
  hasSpaceTheme,
  resolveSpaceRoleSystemKey,
  sanitizeSpaceTheme,
  type CreateSpaceEncryptedSeed,
  type EncryptedSpaceField,
  type PublicSpace,
  type PublicSpaceChannel,
  type PublicSpaceChannelCategory,
  type PublicSpaceRole,
  type SpaceIdentityMediaPointer,
  type SpaceThemeTokens,
  type UpdateSpaceParams,
} from '@adieuu/shared';
import {
  SPACE_SEED_ENTITY,
  type CommunityCipher,
  type SpacePayloadAad,
} from '@adieuu/crypto';
import { getSpaceKey } from '../../services/spaceCipherService';
import { decryptBody, encryptContent, type CipherFields } from './spaceChannelCipher';

export interface SpaceMetadataPlaceholders {
  encryptedSpace: string;
  encryptedChannel: string;
  encryptedRole: string;
}

export type SpaceMetadataKind = 'space' | 'channel' | 'category' | 'role';

export function encryptSpaceMetadataField(
  cipher: CommunityCipher,
  plaintext: string,
  opts: { spaceKey: Uint8Array; aad: SpacePayloadAad },
): EncryptedSpaceField {
  const fields = encryptContent(cipher, plaintext, opts);
  return {
    encryptedName: fields.ciphertext,
    nameNonce: fields.nonce,
    cipherId: fields.cipherId,
  };
}

export async function encryptSpaceMetadataFieldFor(
  cipher: CommunityCipher,
  plaintext: string,
  aad: SpacePayloadAad,
): Promise<EncryptedSpaceField> {
  const spaceKey = await getSpaceKey(cipher, aad.spaceId);
  return encryptSpaceMetadataField(cipher, plaintext, { spaceKey, aad });
}

function metadataAadFallbacks(params: {
  spaceId: string;
  entityId: string;
  field: SpacePayloadAad['field'];
  kind: SpaceMetadataKind;
  systemKey?: PublicSpaceRole['systemKey'];
}): SpacePayloadAad[] {
  const { spaceId, entityId, field, kind, systemKey } = params;
  const primary: SpacePayloadAad = { spaceId, entityId, field };
  const extras: SpacePayloadAad[] = [];
  if (kind === 'channel' && field === 'name') {
    extras.push(
      { spaceId, entityId: SPACE_SEED_ENTITY.channel, field: 'name' },
      { spaceId, entityId: SPACE_SEED_ENTITY.newChannel, field: 'name' },
    );
  }
  if (kind === 'category' && field === 'name') {
    extras.push(
      { spaceId, entityId: SPACE_SEED_ENTITY.category, field: 'name' },
      { spaceId, entityId: SPACE_SEED_ENTITY.newCategory, field: 'name' },
    );
  }
  if (kind === 'role' && field === 'name') {
    if (systemKey === 'admin') {
      extras.push({ spaceId, entityId: SPACE_SEED_ENTITY.roleAdmin, field: 'name' });
    } else if (systemKey === 'everyone') {
      extras.push({ spaceId, entityId: SPACE_SEED_ENTITY.roleEveryone, field: 'name' });
    } else {
      extras.push(
        { spaceId, entityId: SPACE_SEED_ENTITY.roleAdmin, field: 'name' },
        { spaceId, entityId: SPACE_SEED_ENTITY.roleEveryone, field: 'name' },
        { spaceId, entityId: SPACE_SEED_ENTITY.newRole, field: 'name' },
      );
    }
  }
  return [primary, ...extras.filter((a) => a.entityId !== entityId)];
}

export function decryptSpaceMetadataField(
  cipher: CommunityCipher | null | undefined,
  fields: { encryptedName?: string; nameNonce?: string; cipherId?: string } | undefined,
  fallback: string,
  opts: {
    spaceId: string;
    entityId: string;
    field: SpacePayloadAad['field'];
    kind: SpaceMetadataKind;
    systemKey?: PublicSpaceRole['systemKey'];
  },
): string {
  if (!fields?.encryptedName || !fields.nameNonce || !fields.cipherId) {
    return fallback;
  }
  return decryptBody(
    {
      ciphertext: fields.encryptedName,
      nonce: fields.nameNonce,
      cipherId: fields.cipherId,
    },
    cipher,
    fallback,
    {
      aad: metadataAadFallbacks(opts),
    },
  );
}

export function decryptSpaceDescription(
  cipher: CommunityCipher | null | undefined,
  space: Pick<PublicSpace, 'id' | 'encryptedDescription' | 'descriptionNonce' | 'cipherId'>,
  fallback: string,
): string {
  if (!space.encryptedDescription || !space.descriptionNonce || !space.cipherId) {
    return fallback;
  }
  return decryptBody(
    {
      ciphertext: space.encryptedDescription,
      nonce: space.descriptionNonce,
      cipherId: space.cipherId,
    },
    cipher,
    fallback,
    {
      aad: { spaceId: space.id, entityId: space.id, field: 'description' },
    },
  );
}

/** Build client-encrypted seed payloads for default category/channel + system roles. */
export async function buildEncryptedSpaceSeed(
  cipher: CommunityCipher,
  spaceId: string,
): Promise<CreateSpaceEncryptedSeed> {
  const spaceKey = await getSpaceKey(cipher, spaceId);
  const enc = (plaintext: string, entityId: string) =>
    encryptSpaceMetadataField(cipher, plaintext, {
      spaceKey,
      aad: { spaceId, entityId, field: 'name' },
    });
  return {
    category: enc(DEFAULT_SPACE_CATEGORY_NAME, SPACE_SEED_ENTITY.category),
    channel: enc(DEFAULT_SPACE_CHANNEL_NAME, SPACE_SEED_ENTITY.channel),
    roles: [
      {
        system: 'admin',
        ...enc(DEFAULT_ADMIN_ROLE_NAME, SPACE_SEED_ENTITY.roleAdmin),
      },
      {
        system: 'everyone',
        ...enc(DEFAULT_MEMBER_ROLE_NAME, SPACE_SEED_ENTITY.roleEveryone),
      },
    ],
  };
}

/** Resolve a Space display name, decrypting when identity is encrypted. */
export function resolveSpaceDisplayName(
  space: Pick<
    PublicSpace,
    'id' | 'name' | 'slug' | 'encryptIdentity' | 'encryptedName' | 'nameNonce' | 'cipherId'
  >,
  cipher: CommunityCipher | null | undefined,
  placeholders: Pick<SpaceMetadataPlaceholders, 'encryptedSpace'>,
): string {
  if (!space.encryptIdentity) {
    return space.name || space.slug;
  }
  const decrypted = decryptSpaceMetadataField(
    cipher,
    {
      encryptedName: space.encryptedName,
      nameNonce: space.nameNonce,
      cipherId: space.cipherId,
    },
    '',
    { spaceId: space.id, entityId: space.id, field: 'name', kind: 'space' },
  );
  if (decrypted) return decrypted;
  return placeholders.encryptedSpace;
}

/** Resolve a Space description for display. */
export function resolveSpaceDisplayDescription(
  space: Pick<
    PublicSpace,
    | 'id'
    | 'description'
    | 'encryptIdentity'
    | 'encryptedDescription'
    | 'descriptionNonce'
    | 'cipherId'
  >,
  cipher: CommunityCipher | null | undefined,
): string | undefined {
  if (!space.encryptIdentity) {
    return space.description;
  }
  if (!space.encryptedDescription) return undefined;
  const decrypted = decryptSpaceDescription(cipher, space, '');
  return decrypted || undefined;
}

// ---------------------------------------------------------------------------
// Space identity: logo/banner pointers and theme (encryptIdentity Spaces)
// ---------------------------------------------------------------------------

export type SpaceIdentityField = 'name' | 'description' | 'logo' | 'banner' | 'theme';

function spaceIdentityAad(spaceId: string, field: SpaceIdentityField): SpacePayloadAad {
  return { spaceId, entityId: spaceId, field };
}

/** Encrypt one Space identity field with the Space key (AAD binds Space + field). */
export async function encryptSpaceIdentityField(
  cipher: CommunityCipher,
  spaceId: string,
  field: SpaceIdentityField,
  plaintext: string,
): Promise<CipherFields> {
  const spaceKey = await getSpaceKey(cipher, spaceId);
  return encryptContent(cipher, plaintext, { spaceKey, aad: spaceIdentityAad(spaceId, field) });
}

function decryptSpaceIdentityBlob(
  cipher: CommunityCipher | null | undefined,
  fields: { ciphertext?: string; nonce?: string; cipherId?: string },
  spaceId: string,
  field: SpaceIdentityField,
  spaceKey?: Uint8Array,
): string | null {
  if (!cipher || !fields.ciphertext || !fields.nonce || !fields.cipherId) return null;
  const text = decryptBody(
    { ciphertext: fields.ciphertext, nonce: fields.nonce, cipherId: fields.cipherId },
    cipher,
    '',
    {
      aad: spaceIdentityAad(spaceId, field),
      requireCipher: true,
      // New fields never had an untagged form, so there is no legacy path to open.
      allowLegacyUntagged: false,
      ...(spaceKey ? { spaceKey } : {}),
    },
  );
  return text || null;
}

const E2E_MEDIA_ID_PATTERN = /^[A-Za-z0-9_-]{1,200}$/;
const BASE64_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;
const MAX_POINTER_DIMENSION = 8192;

/** Accept only a well-formed pointer; anything else (tampering, bugs) reads as "no image". */
export function parseSpaceMediaPointer(raw: string): SpaceIdentityMediaPointer | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  const p = value as Record<string, unknown>;
  if (typeof p.e2eMediaId !== 'string' || !E2E_MEDIA_ID_PATTERN.test(p.e2eMediaId)) return null;
  if (typeof p.key !== 'string' || !BASE64_PATTERN.test(p.key)) return null;
  if (typeof p.nonce !== 'string' || !BASE64_PATTERN.test(p.nonce)) return null;
  if (
    typeof p.contentType !== 'string' ||
    !(SPACE_IDENTITY_MEDIA_CONTENT_TYPES as readonly string[]).includes(p.contentType)
  ) {
    return null;
  }
  const dim = (v: unknown) =>
    v === undefined || (Number.isInteger(v) && (v as number) > 0 && (v as number) <= MAX_POINTER_DIMENSION);
  if (!dim(p.width) || !dim(p.height)) return null;
  return {
    e2eMediaId: p.e2eMediaId,
    key: p.key,
    nonce: p.nonce,
    contentType: p.contentType,
    ...(p.width !== undefined ? { width: p.width as number } : {}),
    ...(p.height !== undefined ? { height: p.height as number } : {}),
  };
}

/** Decrypt a Space's logo or banner pointer. Null when absent, locked, or invalid. */
export function decryptSpaceMediaPointer(
  cipher: CommunityCipher | null | undefined,
  space: Pick<
    PublicSpace,
    'id' | 'cipherId' | 'encryptedLogo' | 'logoNonce' | 'encryptedBanner' | 'bannerNonce'
  >,
  slot: 'logo' | 'banner',
  spaceKey?: Uint8Array,
): SpaceIdentityMediaPointer | null {
  const fields =
    slot === 'logo'
      ? { ciphertext: space.encryptedLogo, nonce: space.logoNonce, cipherId: space.cipherId }
      : { ciphertext: space.encryptedBanner, nonce: space.bannerNonce, cipherId: space.cipherId };
  const text = decryptSpaceIdentityBlob(cipher, fields, space.id, slot, spaceKey);
  return text ? parseSpaceMediaPointer(text) : null;
}

/** Serialize a theme deterministically (sorted, sanitized) before encrypting. */
export function serializeSpaceTheme(theme: SpaceThemeTokens): string {
  const clean = sanitizeSpaceTheme(theme);
  const sorted: Record<string, string> = {};
  for (const key of Object.keys(clean).sort()) sorted[key] = clean[key as keyof SpaceThemeTokens]!;
  return JSON.stringify(sorted);
}

/**
 * Decrypt a Space's encrypted theme. The server never saw its contents, so the
 * result is re-validated as strict hex before it can reach inline styles.
 */
export function decryptSpaceTheme(
  cipher: CommunityCipher | null | undefined,
  space: Pick<PublicSpace, 'id' | 'cipherId' | 'encryptedTheme' | 'themeNonce'>,
  spaceKey?: Uint8Array,
): SpaceThemeTokens | null {
  const text = decryptSpaceIdentityBlob(
    cipher,
    { ciphertext: space.encryptedTheme, nonce: space.themeNonce, cipherId: space.cipherId },
    space.id,
    'theme',
    spaceKey,
  );
  if (!text) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  const result = SpaceThemeSchema.safeParse(parsed);
  if (!result.success) return null;
  const theme = sanitizeSpaceTheme(result.data);
  return hasSpaceTheme(theme) ? theme : null;
}

/** The Space's saved theme (plaintext or decrypted), or null when it has none / is locked. */
export function resolveSpaceTheme(
  space: Pick<
    PublicSpace,
    'id' | 'encryptIdentity' | 'theme' | 'cipherId' | 'encryptedTheme' | 'themeNonce'
  >,
  cipher: CommunityCipher | null | undefined,
  spaceKey?: Uint8Array,
): SpaceThemeTokens | null {
  if (!space.encryptIdentity) {
    const theme = sanitizeSpaceTheme(space.theme);
    return hasSpaceTheme(theme) ? theme : null;
  }
  return decryptSpaceTheme(cipher, space, spaceKey);
}

export interface SpaceProfileDraft {
  name?: string;
  description?: string;
  logo?: SpaceIdentityMediaPointer;
  banner?: SpaceIdentityMediaPointer;
  removeLogo?: boolean;
  removeBanner?: boolean;
}

/**
 * Build the PATCH body for an `encryptIdentity` Space profile edit: every
 * field is encrypted with the Space key and tagged with the Space's cipher.
 */
export async function buildEncryptedSpaceProfilePatch(
  cipher: CommunityCipher,
  spaceId: string,
  draft: SpaceProfileDraft,
): Promise<UpdateSpaceParams> {
  const patch: UpdateSpaceParams = {};
  let cipherId: string | undefined;
  const enc = async (field: SpaceIdentityField, plaintext: string) => {
    const fields = await encryptSpaceIdentityField(cipher, spaceId, field, plaintext);
    cipherId = fields.cipherId;
    return fields;
  };

  if (draft.name !== undefined) {
    const f = await enc('name', draft.name);
    patch.encryptedName = f.ciphertext;
    patch.nameNonce = f.nonce;
  }
  if (draft.description !== undefined) {
    const f = await enc('description', draft.description);
    patch.encryptedDescription = f.ciphertext;
    patch.descriptionNonce = f.nonce;
  }
  if (draft.removeLogo) {
    patch.removeLogo = true;
  } else if (draft.logo) {
    const f = await enc('logo', JSON.stringify(draft.logo));
    patch.encryptedLogo = f.ciphertext;
    patch.logoNonce = f.nonce;
    patch.logoE2eMediaId = draft.logo.e2eMediaId;
  }
  if (draft.removeBanner) {
    patch.removeBanner = true;
  } else if (draft.banner) {
    const f = await enc('banner', JSON.stringify(draft.banner));
    patch.encryptedBanner = f.ciphertext;
    patch.bannerNonce = f.nonce;
    patch.bannerE2eMediaId = draft.banner.e2eMediaId;
  }
  if (cipherId) patch.cipherId = cipherId;
  return patch;
}

/** Build the PATCH body for an `encryptIdentity` Space theme save. */
export async function buildEncryptedSpaceThemePatch(
  cipher: CommunityCipher,
  spaceId: string,
  theme: SpaceThemeTokens,
): Promise<UpdateSpaceParams> {
  if (!hasSpaceTheme(theme)) return { clearTheme: true };
  const f = await encryptSpaceIdentityField(cipher, spaceId, 'theme', serializeSpaceTheme(theme));
  return { encryptedTheme: f.ciphertext, themeNonce: f.nonce, cipherId: f.cipherId };
}

/** Resolve a channel or category display name, decrypting when ciphertext is present. */
export function resolveChannelDisplayName(
  channel: Pick<
    PublicSpaceChannel | PublicSpaceChannelCategory,
    'id' | 'spaceId' | 'name' | 'encryptedName' | 'nameNonce' | 'cipherId'
  >,
  cipher: CommunityCipher | null | undefined,
  placeholders: Pick<SpaceMetadataPlaceholders, 'encryptedChannel'>,
  kind: 'channel' | 'category' = 'channel',
): string {
  if (channel.encryptedName && channel.nameNonce && channel.cipherId) {
    const decrypted = decryptSpaceMetadataField(
      cipher,
      {
        encryptedName: channel.encryptedName,
        nameNonce: channel.nameNonce,
        cipherId: channel.cipherId,
      },
      '',
      {
        spaceId: channel.spaceId,
        entityId: channel.id,
        field: 'name',
        kind,
      },
    );
    if (decrypted) return decrypted;
    return placeholders.encryptedChannel;
  }
  return channel.name;
}

/** Resolve a role display name, decrypting when ciphertext is present. */
export function resolveRoleDisplayName(
  role: Pick<
    PublicSpaceRole,
    | 'id'
    | 'spaceId'
    | 'name'
    | 'encryptedName'
    | 'nameNonce'
    | 'cipherId'
    | 'systemKey'
    | 'isSystem'
    | 'isDefaultMember'
    | 'permissions'
  >,
  cipher: CommunityCipher | null | undefined,
  placeholders: Pick<SpaceMetadataPlaceholders, 'encryptedRole'>,
): string {
  const systemKey = resolveSpaceRoleSystemKey(role);
  const systemFallback =
    systemKey === 'admin'
      ? DEFAULT_ADMIN_ROLE_NAME
      : systemKey === 'everyone'
        ? DEFAULT_MEMBER_ROLE_NAME
        : '';

  if (role.encryptedName && role.nameNonce && role.cipherId) {
    const decrypted = decryptSpaceMetadataField(
      cipher,
      {
        encryptedName: role.encryptedName,
        nameNonce: role.nameNonce,
        cipherId: role.cipherId,
      },
      '',
      {
        spaceId: role.spaceId,
        entityId: role.id,
        field: 'name',
        kind: 'role',
        systemKey: role.systemKey,
      },
    );
    if (decrypted) return decrypted;
    return systemFallback || placeholders.encryptedRole;
  }
  const plaintext = role.name.trim();
  if (plaintext) return plaintext;
  return systemFallback || role.name;
}
